/**
 * Members Service
 * API calls for members management
 */

import apiClient from '../lib/axios';
import type { Member, Role } from '../types';

export interface MemberFormData {
  first_name: string;
  last_name: string;
  email: string;
  gender: 'male' | 'female';
  date_of_birth: string | null;
  position: string | null;
  team_id: number | null;
  date_joined: string;
  biography?: string;
  profile_picture?: File;
}

export interface BulkImportMember {
  first_name: string;
  last_name: string;
  email: string;
  gender: 'male' | 'female';
  date_of_birth: string;
  position: string;
  team_id: number;
  date_joined: string;
  biography?: string;
}

export interface BulkImportResult {
  success: boolean;
  total: number;
  imported: number;
  errors: Array<{
    index: number;
    email: string;
    error: string;
  }>;
}

// New Bulk Import interfaces for CSV + ZIP feature
export interface UploadResponse {
  upload_id: string;
  timestamp: string;
  csv: {
    filename: string;
    size: number;
    size_mb: number;
  };
  zip: {
    filename: string;
    size: number;
    size_mb: number;
  } | null;
}

export interface ValidationError {
  row: number;
  field: string;
  message: string;
}

export interface ValidationWarning {
  row: number;
  message: string;
}

export interface ValidationResponse {
  upload_id: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  can_import: boolean;
  status: 'valid' | 'has_errors';
}

export interface ImportResponse {
  upload_id: string;
  report_id: string;
  total_rows: number;
  imported: number;
  failed: number;
  report_url: string;
  status: 'completed';
}

// Pagination interfaces
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: PaginationMeta;
}

export interface MembersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  team_id?: string | number; // Supports single ID or comma-separated IDs
  gender?: 'male' | 'female';
  status?: 'active' | 'removed' | 'all';
  export?: boolean; // Bypasses pagination limit for CSV export
}

export interface CheckLinkedUserResponse {
  success: boolean;
  has_linked_user: boolean;
  user_id: number | null;
  /** Role of the linked active account (TW-399), null when there is none. */
  user_role?: Role | null;
}

export const membersService = {
  /**
   * Get paginated members with filters
   */
  getPaginated: async (params: MembersQueryParams = {}): Promise<PaginatedResponse<Member>> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.sort) queryParams.set('sort', params.sort);
    if (params.team_id) queryParams.set('team_id', String(params.team_id));
    if (params.gender) queryParams.set('gender', params.gender);
    if (params.status) queryParams.set('status', params.status);
    if (params.export) queryParams.set('export', 'true');

    const queryString = queryParams.toString();
    const url = queryString ? `/members?${queryString}` : '/members';

    const { data } = await apiClient.get<PaginatedResponse<Member>>(url);
    return data;
  },

  /**
   * Get all members (legacy - for backwards compatibility)
   * @deprecated Use getPaginated instead
   */
  getAll: async (): Promise<Member[]> => {
    // Fetch all pages to get all members
    const allMembers: Member[] = [];
    let currentPage = 1;
    let totalPages = 1;

    do {
      const { data } = await apiClient.get<PaginatedResponse<Member>>(
        `/members?limit=100&page=${currentPage}`
      );

      // Handle both old array response and new paginated response
      if (Array.isArray(data)) {
        return data;
      }

      if (data && Array.isArray(data.data)) {
        allMembers.push(...data.data);
        totalPages = data.meta.total_pages;
        currentPage++;
      } else {
        console.error('API returned unexpected data format for members:', data);
        break;
      }
    } while (currentPage <= totalPages);

    return allMembers;
  },

  /**
   * Get member by ID
   */
  getById: async (id: number): Promise<Member> => {
    const { data } = await apiClient.get<Member>(`/member/${id}`);
    return data;
  },

  /**
   * Create new member
   */
  create: async (memberData: MemberFormData): Promise<Member> => {
    const formData = new FormData();

    // Add all fields to FormData
    Object.entries(memberData).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'profile_picture' && value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value === null ? '' : String(value));
        }
      }
    });

    const { data } = await apiClient.post<Member>('/members', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  /**
   * Update member
   * Note: Using POST instead of PUT because PHP doesn't support multipart/form-data for PUT requests
   */
  update: async (id: number, memberData: MemberFormData): Promise<Member> => {
    const formData = new FormData();

    // Add all fields to FormData
    Object.entries(memberData).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'profile_picture' && value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value === null ? '' : String(value));
        }
      }
    });

    const { data } = await apiClient.post<Member>(`/member/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  /**
   * Check if member has linked active user before removal
   */
  checkLinkedUser: async (id: number): Promise<CheckLinkedUserResponse> => {
    const { data } = await apiClient.delete<CheckLinkedUserResponse>(`/member/${id}?check_user=1`);
    return data;
  },

  /**
   * Remove member (soft-delete)
   * @param id Member ID
   * @param force If true, auto-deactivates linked user account
   */
  delete: async (id: number, force: boolean = false): Promise<void> => {
    await apiClient.delete(`/member/${id}${force ? '?force=1' : ''}`);
  },

  /**
   * Restore a removed member
   */
  restore: async (id: number): Promise<void> => {
    await apiClient.post(`/member/${id}?action=restore`);
  },

  /**
   * Bulk import members
   */
  bulkImport: async (members: BulkImportMember[]): Promise<BulkImportResult> => {
    const { data } = await apiClient.post<BulkImportResult>(
      '/members?action=bulk_import',
      { members }
    );
    return data;
  },

  /**
   * Get member profile image URL
   */
  getImageUrl: (memberId: number): string => {
    return `${apiClient.defaults.baseURL}/image/${memberId}`;
  },

  /**
   * Fetch member image as blob URL (with JWT authentication)
   * Images are fetched via axios which includes JWT token in Authorization header
   */
  fetchMemberImage: async (memberId: number): Promise<string | null> => {
    try {
      // Check cache first
      const cacheKey = `member_${memberId}`;
      if (imageCache.has(cacheKey)) {
        return imageCache.get(cacheKey)!;
      }

      // Fetch image as blob with JWT authentication
      const response = await apiClient.get<Blob>(`/image/${memberId}`, {
        responseType: 'blob',
        params: { t: Date.now() }, // Cache busting
      });

      // Create blob URL
      const blobUrl = URL.createObjectURL(response.data);

      // Cache the blob URL
      imageCache.set(cacheKey, blobUrl);

      return blobUrl;
    } catch (error) {
      console.error(`Failed to load image for member ${memberId}:`, error);
      return null;
    }
  },

  /**
   * Revoke blob URL and remove from cache
   */
  revokeMemberImage: (memberId: number): void => {
    const cacheKey = `member_${memberId}`;
    const blobUrl = imageCache.get(cacheKey);

    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      imageCache.delete(cacheKey);
    }
  },

  /**
   * Clear all cached images
   */
  clearImageCache: (): void => {
    imageCache.forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    imageCache.clear();
  },

  // ========== Bulk Import Methods (CSV + ZIP) ==========

  /**
   * Download CSV template for bulk import
   */
  downloadTemplate: async (): Promise<Blob> => {
    const response = await apiClient.get('/member_imports/import/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload CSV and ZIP files for bulk import
   * @param csv CSV file containing member data
   * @param zip Optional ZIP file containing member photos
   */
  uploadBulkImportFiles: async (csv: File, zip: File | null): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('csv', csv);
    if (zip) {
      formData.append('zip', zip);
    }

    const { data } = await apiClient.post<UploadResponse>(
      '/member_imports/import/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return data;
  },

  /**
   * Validate uploaded CSV file
   * @param uploadId Upload ID from upload response
   */
  validateBulkImport: async (uploadId: string): Promise<ValidationResponse> => {
    const { data } = await apiClient.post<ValidationResponse>(
      '/member_imports/import/validate',
      { upload_id: uploadId }
    );

    return data;
  },

  /**
   * Execute bulk import
   * @param uploadId Upload ID from upload response
   */
  executeBulkImport: async (uploadId: string): Promise<ImportResponse> => {
    const { data } = await apiClient.post<ImportResponse>(
      '/member_imports/import/execute',
      { upload_id: uploadId }
    );

    return data;
  },

  /**
   * Download import report CSV
   * @param reportId Report ID from import response
   */
  downloadImportReport: async (reportId: string): Promise<Blob> => {
    const response = await apiClient.get(`/member_imports/import/report/${reportId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Image cache for blob URLs
const imageCache = new Map<string, string>();
